/**
 * Stream Protocol — Message types for persistent chat via durable streams.
 *
 * Discriminated union on the `type` field. Every message includes
 * conversationId and a unique messageId for frontend deduplication.
 */

import { z } from 'zod';
import { TransparencyStepSchema } from './transparency';
import type { TransparencyStep } from './transparency';
import { logger } from '../lib/logger';

const envelope = {
  messageId: z.string(),
  conversationId: z.string(),
  timestamp: z.string(),
  // Turn-keyed fields (bead terra-design-v2-chat-yub.14, Q6 spec #7): every
  // in-turn frame carries these; only the pre-turn `metadata` frame omits
  // them. Declared ONCE here on the shared envelope, never per-variant.
  // `attempt` keeps master's stricter `.int()` (added in 49589ff32/6c2bbdb8e):
  // the real staging trace only ever carries integers here, so the narrower
  // type is both correct and the one the rest of master already assumes.
  turnId: z.string().optional(),
  attempt: z.number().int().optional(),
  turnEpoch: z.number().int().optional(),
};

/**
 * Steps are diagnostics, never the message — a bad step may cost you the
 * step, never the answer. `stats.steps` rides on the SAME `assistant` frame
 * that carries the rep-visible answer text, and `TransparencyStepSchema` is
 * a strict discriminated union over `type`; with a plain `z.array(...)`, one
 * step of an unknown/future `type` (or missing a required field) fails the
 * whole array, which fails `safeParse` on the ENTIRE enclosing frame — the
 * answer silently never renders. Parse per-element instead: keep every step
 * that parses, drop the ones that don't, and never fail the enclosing frame.
 * Dropped steps are logged once per frame at debug level so this never
 * silently hides a real upstream contract change.
 */
const TolerantStepsSchema = z.array(z.unknown()).transform((raw): TransparencyStep[] => {
  const steps: TransparencyStep[] = [];
  let dropped = 0;
  for (const candidate of raw) {
    const result = TransparencyStepSchema.safeParse(candidate);
    if (result.success) steps.push(result.data);
    else dropped += 1;
  }
  if (dropped > 0) {
    logger.debug(
      { dropped, kept: steps.length, total: raw.length },
      '[stream-protocol] dropped malformed transparency step(s) from a frame — the step was dropped, not the answer',
    );
  }
  return steps;
});

export const StreamMessageSchema = z.discriminatedUnion('type', [
  z.looseObject({
    type: z.literal('metadata'),
    ...envelope,
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    selectedAccountId: z.string().nullable(),
  }),
  z.looseObject({ type: z.literal('user'), ...envelope, content: z.string() }),
  z.looseObject({ type: z.literal('status'), ...envelope, message: z.string() }),
  z.looseObject({
    type: z.literal('assistant'),
    ...envelope,
    content: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    stats: z.looseObject({
      turns: z.number().optional(),
      duration_ms: z.number().optional(),
      extraction_ms: z.number().optional(),
      qa_ms: z.number().optional(),
      steps: TolerantStepsSchema.optional(),
    }).optional(),
  }),
  z.looseObject({
    type: z.literal('assistant_delta'),
    ...envelope,
    parentMessageId: z.string(),
    delta: z.string(),
  }),
  z.looseObject({
    type: z.literal('error'),
    ...envelope,
    code: z.string(),
    message: z.string(),
  }),
  z.looseObject({ type: z.literal('steps_update'), ...envelope, steps: TolerantStepsSchema }),
  z.looseObject({
    type: z.literal('turn_superseded'),
    ...envelope,
    turnId: z.string(),
    supersededAttempt: z.number().int(),
  }),
  // End-of-turn control frame, best-effort: Keystone can die between commit and
  // append, so consumers keep their timeouts. Never rendered, never counted.
  // `outcome` is a plain string so a new Keystone outcome still ends the turn.
  z.looseObject({
    type: z.literal('turn_done'),
    ...envelope,
    turnId: z.string(),
    attempt: z.number().int(),
    outcome: z.string(),
  }),
]);

export type StreamMessage = z.infer<typeof StreamMessageSchema>;
export type UserMessage = Extract<StreamMessage, { type: 'user' }>;
export type AssistantMessage = Extract<StreamMessage, { type: 'assistant' }>;
export type ErrorMessage = Extract<StreamMessage, { type: 'error' }>;
export type TurnSupersededMessage = Extract<StreamMessage, { type: 'turn_superseded' }>;
export type TurnDoneMessage = Extract<StreamMessage, { type: 'turn_done' }>;
